import { AgencyDetail, AgencyRebate, Actor } from 'core';
import { AgencyStatus } from 'core/agency/Agency';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AgencyManager, DefaultAgencyManager } from 'core/agency/AgencyManager';
import { multiply100 } from 'utils/Math';
import {
  AgencyMemberTabModel,
  DefaultAgencyMemberTabModel
} from './AgencyMemberTabModel';
import { round } from 'lodash';
import { AgencyAuditLogModel } from 'containers/AuditLog/AgencyAuditLogModel';
import i18n from 'i18n';
import { toast } from 'react-toastify';

export type AgencyDetailModalData = {
  title: string;
  message: string;
  confirmBtnData: {
    title: string;
    callback: () => void;
  };
};

export interface AgencyDetailModel {
  readonly agencyId?: number;
  readonly operator?: Actor | null;
  readonly defaultAgency?: AgencyDetail & AgencyRebate;
  readonly title: string;
  readonly currentAccountId: number;
  readonly status: AgencyStatus;
  state: AgencyDetailState;
  isSystemViewOnly: boolean;
  event: UpdateEventListener<AgencyDetailModel>;
  init(): Promise<void>;
  getAgencyMemberTabModel(): AgencyMemberTabModel;
  showTab(tabName: string | null, e): void;
  getAuditLogModel(): AgencyAuditLogModel | undefined;
  showArchiveConfirmModal(): void;
  restoreAgency(): Promise<void>;
  hideModal(): void;
}

export interface AgencyDetailProps {
  readonly model: AgencyDetailModel;
}

export type AgencyDetailState = {
  isLoading: boolean;
  isMutating: boolean;
  dataTabName: string;
  status: AgencyStatus;
  modalData?: AgencyDetailModalData;
};

export class DefaultAgencyDetailModel implements AgencyDetailModel {
  agencyDetail?: AgencyDetail & AgencyRebate;
  modelEvent: FireableUpdateEventListener<AgencyDetailModel>;
  isLoading: boolean;
  isSystemViewOnly: boolean;
  agencyManager: AgencyManager;
  agencyId: number;
  operator: Actor | null;
  agencyMemberTabModel?: AgencyMemberTabModel;
  dataTabName = 'basic';
  auditLogModel?: AgencyAuditLogModel;
  agencyStatus: AgencyStatus = AgencyStatus.ACTIVE;
  modalData?: AgencyDetailModalData;
  isMutating = false;

  constructor(
    agencyId: number,
    operator: Actor | null,
    public currentAccountId: number,
    agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    this.agencyId = agencyId;
    this.operator = operator;
    this.agencyManager = agencyManager;
    this.modelEvent = new FireableUpdateEventListener<AgencyDetailModel>();
    this.isLoading = true;
    this.isSystemViewOnly = operator ? operator.role === 'ROOT' : false;
  }

  async init(): Promise<void> {
    try {
      const { detail, rebate, status } = await this.agencyManager.fetchAgency(
        this.agencyId
      );
      this.agencyStatus = status;
      const percentFields = this.convertToPercent({ ...detail, ...rebate });
      this.agencyDetail = {
        ...detail,
        ...percentFields,
        effectDate: rebate.effectDate
      };
      await this.getAuditLogModel()?.init(1, true);
      this.updateState(false);
    } catch (e) {}
  }

  showTab = (tabName, e) => {
    e && e.stopPropagation();
    if (tabName === null) {
      return;
    }
    this.dataTabName = tabName;
    this.updateState(false);
  };

  convertToPercent({
    sysPercentIn,
    sysPercentOut,
    sysPercent,
    sysDataPercent,
    agcPercent,
    agcDataPercent,
    taxPercent,
    addonProps,
    currentRate,
    futureRate
  }: AgencyDetail & AgencyRebate) {
    const { maxOrderProfit } = addonProps;
    const multiply = (value) => round(multiply100(value), 2);
    return {
      sysPercentIn: multiply(sysPercentIn),
      sysPercentOut: multiply(sysPercentOut),
      sysPercent: multiply(sysPercent),
      sysDataPercent: multiply(sysDataPercent),
      agcPercent: multiply(agcPercent),
      agcDataPercent: multiply(agcDataPercent),
      taxPercent: multiply(taxPercent),
      currentRate: multiply(currentRate),
      futureRate: futureRate ? multiply(futureRate) : futureRate,
      addonProps: {
        ...addonProps,
        maxOrderProfit: multiply(maxOrderProfit)
      }
    };
  }

  get defaultAgency(): (AgencyDetail & AgencyRebate) | undefined {
    return this.agencyDetail;
  }

  get title(): string {
    return i18n.t('agencyDetail.labels.title');
  }

  get status(): AgencyStatus {
    return this.agencyStatus;
  }

  get state(): AgencyDetailState {
    return {
      isLoading: this.isLoading,
      isMutating: this.isMutating,
      dataTabName: this.dataTabName,
      status: this.agencyStatus,
      modalData: this.modalData
    };
  }

  showArchiveConfirmModal = () => {
    this.modalData = {
      title: i18n.t('agencyDetail.archiveModal.title'),
      message: i18n.t('agencyDetail.archiveModal.content'),
      confirmBtnData: {
        title: i18n.t('common.buttons.archive'),
        callback: this.archiveAgency
      }
    };
    this.modelEvent.fireEvent(this);
  };

  hideModal = () => {
    this.modalData = undefined;
    this.modelEvent.fireEvent(this);
  };

  archiveAgency = async () => {
    // 用疊加式的 isMutating（而非會 early-return 整頁的 isLoading），
    // 讓確認 Modal 送出中仍持續 render、不被 LoadingIndicator 取代
    this.setMutating(true);
    try {
      await this.agencyManager.archiveAgency(this.agencyId);
      // API 為 void，狀態可預期，本地直接設定並即時切換按鈕
      this.agencyStatus = AgencyStatus.ARCHIVED;
      toast.success(i18n.t('common.messages.succeeded'));
      // 封存會一併移除底下成員的角色，成員清單要立刻重抓，
      // 否則不重新整理的話畫面會繼續列出已經不存在的成員。
      // 使用者還沒點過成員分頁時 model 尚未建立，刻意不為了刷新而把它建出來。
      await this.agencyMemberTabModel?.fetch();
    } catch (e) {}
    this.modalData = undefined;
    this.setMutating(false);
  };

  restoreAgency = async () => {
    this.setMutating(true);
    try {
      await this.agencyManager.restoreAgency(this.agencyId);
      this.agencyStatus = AgencyStatus.ACTIVE;
      toast.success(i18n.t('common.messages.succeeded'));
    } catch (e) {}
    this.modalData = undefined;
    this.setMutating(false);
  };

  get event(): UpdateEventListener<AgencyDetailModel> {
    return this.modelEvent;
  }

  updateState(isLoading: boolean) {
    this.isLoading = isLoading;
    this.modelEvent.fireEvent(this);
  }

  setMutating(isMutating: boolean) {
    this.isMutating = isMutating;
    this.modelEvent.fireEvent(this);
  }

  getAgencyMemberTabModel(): AgencyMemberTabModel {
    if (this.agencyMemberTabModel) {
      return this.agencyMemberTabModel;
    }
    this.agencyMemberTabModel = new DefaultAgencyMemberTabModel(
      this.agencyDetail!,
      this.agencyManager,
      this.operator,
      this.currentAccountId
    );
    return this.agencyMemberTabModel;
  }

  getAuditLogModel(): AgencyAuditLogModel | undefined {
    // TODO: temparary only Admin enable to see audit log
    const isAdmin = this.isSystemViewOnly;
    if (!isAdmin) {
      return undefined;
    }

    if (this.auditLogModel && this.agencyId === this.auditLogModel.agencyId) {
      return this.auditLogModel;
    }

    this.auditLogModel = new AgencyAuditLogModel(isAdmin, this.agencyId);
    return this.auditLogModel;
  }
}

export class RestaurantDetailModel extends DefaultAgencyDetailModel {
  get title(): string {
    return i18n.t('restaurantDetail.labels.title');
  }
}
